/**
 * Keeping a hangar screen alive all day without anyone touching it (Kyle, 2026-10-03: "minimum actively
 * updating the screen every minute without anyone touching it all day. never going stale or idle").
 *
 * The screen already checks for new readings every few seconds (`refresh.ts`). What could still leave a TV
 * showing old weather is everything around that loop:
 *
 * - **The TV or browser going to sleep.** The screen holds a wake lock, taken again whenever the page comes
 *   back into view (browsers drop it when a page is hidden).
 * - **A screen stuck on old code.** The page used to load once and run for weeks, so a screen opened before a
 *   deploy kept the old behaviour forever. Each build now publishes `/version.json`; the screen compares it
 *   with its own build and reloads itself when they differ, and reloads once a day regardless.
 * - **Readings that only change every 20 minutes.** An airport's automated station publishes on a fixed
 *   cadence (Fort Morgan's every 20 minutes), and no amount of checking makes it faster. The screen says so:
 *   how long ago it last checked, and when the next report is due, so a screen whose numbers have not moved
 *   is visibly alive rather than looking frozen.
 *
 * A reload never happens while SkyWeather is unreachable or the new page cannot be fetched: reloading then
 * would replace the last good readings with an error page.
 */

/** This build's id, stamped by Vite (`vite.config.ts`). */
export const BUILD_ID: string = (import.meta.env['VITE_BUILD_ID'] as string | undefined) ?? 'dev'

/** How often to ask whether a newer build has been deployed. */
export const VERSION_CHECK_MS = 5 * 60_000

/** Reload at least this often anyway, to clear anything a long-running page accumulates. */
export const DAILY_RELOAD_MS = 24 * 60 * 60_000

export interface ReloadInputs {
  /** The build id `/version.json` reports, or null when it could not be fetched. */
  servedBuildId: string | null
  /** This page's own build id. */
  runningBuildId: string
  /** When this page was opened. */
  openedAt: number
  now: number
  /** True while the last check for weather succeeded: reloading during an outage would blank the screen. */
  apiReachable: boolean
}

/** Why the screen should reload itself now, or null to keep running. */
export function reloadReason(i: ReloadInputs): 'new-build' | 'daily' | null {
  // The page itself must be fetchable (version.json answered) and the weather must be flowing, or a reload
  // would swap good readings for an error page.
  if (i.servedBuildId === null || !i.apiReachable) return null
  if (i.runningBuildId !== 'dev' && i.servedBuildId !== i.runningBuildId) return 'new-build'
  if (i.now - i.openedAt >= DAILY_RELOAD_MS) return 'daily'
  return null
}

/** The build id the site is serving now, or null if it could not be read. */
export async function fetchServedBuildId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchImpl('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const body = (await res.json()) as { build?: unknown }
    return typeof body.build === 'string' && body.build !== '' ? body.build : null
  } catch {
    return null
  }
}

/** The shortest and longest cadence believed when inferring how often a source reports. */
const MIN_CADENCE_MS = 60_000
const MAX_CADENCE_MS = 2 * 60 * 60_000

/**
 * How often a source reports, read from its recent readings: the median gap between them, or null when
 * there are too few readings or the gaps are not a believable cadence. The median, not the mean, so one
 * special report between two routine ones does not halve the answer.
 */
export function reportCadenceMs(observedAt: readonly number[]): number | null {
  if (observedAt.length < 3) return null
  const gaps: number[] = []
  for (let k = 1; k < observedAt.length; k++) {
    const gap = observedAt[k]! - observedAt[k - 1]!
    if (gap > 0) gaps.push(gap)
  }
  if (gaps.length < 2) return null
  gaps.sort((a, b) => a - b)
  const mid = Math.floor(gaps.length / 2)
  const median = gaps.length % 2 === 1 ? gaps[mid]! : (gaps[mid - 1]! + gaps[mid]!) / 2
  if (median < MIN_CADENCE_MS || median > MAX_CADENCE_MS) return null
  // Round to the minute: stations report on the minute, and "every 19.5 min" helps nobody.
  return Math.round(median / 60_000) * 60_000
}

/**
 * When the next report is due: the last reading plus the cadence. Null when the cadence is unknown or the
 * due time is well past (then the source is late, which the health badge already says).
 */
export function nextReportDue(observedAt: readonly number[], now: number): { cadenceMs: number; dueAt: number } | null {
  const cadenceMs = reportCadenceMs(observedAt)
  const last = observedAt[observedAt.length - 1]
  if (cadenceMs === null || last === undefined) return null
  const dueAt = last + cadenceMs
  if (now > dueAt + cadenceMs / 2) return null
  return { cadenceMs, dueAt }
}

/** "checked 2 s ago" — how long since the screen last asked SkyWeather, whether or not anything changed. */
export function checkedText(lastCheckAt: number | null, now: number): string {
  if (lastCheckAt === null) return 'checking…'
  const s = Math.max(0, Math.round((now - lastCheckAt) / 1000))
  return s < 60 ? `checked ${s} s ago` : `checked ${Math.floor(s / 60)} min ago`
}

/** The part of the Screen Wake Lock API used here (not in every TypeScript DOM library yet). */
interface WakeLockSentinelLike {
  released: boolean
  release(): Promise<void>
}
interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

/**
 * Keep the display from sleeping while the screen is shown. Browsers release the lock whenever the page is
 * hidden, so it is taken again each time the page becomes visible. Browsers without the API (or that refuse
 * it) are left alone: the TV's own power settings then apply. Returns a function that stops holding it.
 */
export function holdScreenAwake(doc: Document = document, nav: Navigator = navigator): () => void {
  const wakeLock = (nav as Navigator & { wakeLock?: WakeLockLike }).wakeLock
  if (!wakeLock) return () => {}
  let sentinel: WakeLockSentinelLike | null = null
  let stopped = false
  const take = () => {
    if (stopped || doc.visibilityState !== 'visible' || (sentinel !== null && !sentinel.released)) return
    wakeLock.request('screen').then(
      (s) => {
        if (stopped) void s.release()
        else sentinel = s
      },
      () => {
        // Refused (battery saver, no user gesture on some browsers): the next visibility change tries again.
      },
    )
  }
  doc.addEventListener('visibilitychange', take)
  take()
  return () => {
    stopped = true
    doc.removeEventListener('visibilitychange', take)
    if (sentinel !== null && !sentinel.released) void sentinel.release()
  }
}
